'use client';

import type { ICollectionPipelineStatusV3 } from '@repo/sdk';
import { preheatCollectionCropsV1Api } from '@repo/sdk';
import { useRouter } from 'next/navigation';
import type { JSX } from 'react';
import { useCallback, useState } from 'react';
import { PipelineStepBadge } from '../pipeline-step-badge';
import { PipelineStepCell } from '../pipeline-step-cell';
import { PipelineStepDialog } from '../pipeline-step-dialog';
import type { IStepActionStatus } from '../use-step-action';
import { StepLogPanel } from './step-log-panel';

interface ICropStepProps {
  item: ICollectionPipelineStatusV3;
}

export function CropStep({ item }: ICropStepProps): JSX.Element {
  const { clipsCropped } = item.steps;
  const router = useRouter();
  const [status, setStatus] = useState<IStepActionStatus>('idle');
  const [log, setLog] = useState('');

  const run = useCallback(async () => {
    setStatus('running');
    setLog('Enqueueing crops on Snow Titan…\n');
    try {
      const res = await preheatCollectionCropsV1Api({
        collectionId: item.collectionId,
        limit: 0,
      });
      const data = res.data?.data;
      if (!data) {
        setLog((p) => `${p}[error] empty response\n`);
        setStatus('error');
        return;
      }
      setLog(
        (p) =>
          `${p}Enqueued: ${data.enqueuedCount}\nDuplicate (already in flight): ${data.duplicateCount}\nSkipped (no vault path / bad timing): ${data.skippedCount}\n`,
      );
      setStatus('done');
      router.refresh();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setLog((p) => `${p}[error] ${message}\n`);
      setStatus('error');
    }
  }, [item.collectionId, router]);

  return (
    <PipelineStepCell
      badge={<PipelineStepBadge step={clipsCropped} />}
      renderDialog={(open, onOpenChange) => (
        <PipelineStepDialog
          open={open}
          onOpenChange={onOpenChange}
          title="Cropped"
          description={`Vertical crops produced for triage clips. Auto-runs after the Clip step. Use "Crop all clips" to manually enqueue every pending/failed crop in this collection on Snow Titan. ${clipsCropped.done}/${clipsCropped.total} cropped.`}
          dataHref={`/sys/media?collectionId=${item.collectionId}`}
          dataLabel="Open clips"
          primaryAction={{
            label: 'Crop all clips',
            onClick: run,
            pending: status === 'running',
          }}
          body={<StepLogPanel status={status} log={log} />}
        />
      )}
    />
  );
}
